// ATENCIÓN DEL CENTRO (V3) — una fila POR CASO ABIERTO del sitio autorizado, con la unidad
// declarada. Es independiente de búsqueda, selección, cantidad, página y plegado: lo que se filtra
// son las habitaciones, nunca los casos. Altura CONTROLADA: si no caben todos, se dice cuántos
// faltan y se accede a ellos; la lista no crece hasta tapar el centro.
// No toma, no libera, no verifica, no escala y no cierra: eso ocurre en Central.
import { useState } from "react";
import { ArrowUpRight, Wrench, MapPin, ChevronDown, ChevronUp, ShieldCheck, UserRound, CircleAlert, Bell } from "lucide-react";
import { visualPriority as acentoPersona, eventSentence } from "@/components/visualV2/presentation";
import { relojAlarma } from "@/lib/centroV3/data";

const VISIBLES_COLAPSADO = 6;

function Fila({ c, nowMs, onAbrirCaso, onVerHabitacion, onSeleccionar, seleccionado }) {
  const ac = acentoPersona(c.prioridad);
  return (
    <article data-testid={`radar-inst-caso-${c.alarma_id}`} data-prioridad={c.prioridad} data-priority={ac.key}
      data-tecnica={c.es_tecnica ? "si" : "no"}
      className={`gv2-card flex min-w-0 flex-col gap-1.5 px-3 py-2.5 ${seleccionado ? "gv2-selected" : ""}`}
      >
      <div className="flex items-start justify-between gap-2">
        <button type="button" data-testid={`radar-inst-caso-sel-${c.alarma_id}`}
          onClick={() => onSeleccionar(c)}
          className="min-w-0 flex-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]">
          <span className="gv2-card-head">
            <span className="gv2-card-labels">
              <span className="gv2-priority-badge">{ac.key === "alta" ? <CircleAlert aria-hidden="true" /> : <Bell aria-hidden="true" />}{ac.label}</span>
              <span className="gv2-state">{c.pendiente ? "Sin tomar" : c.estadoLabel}</span>
            </span>
            <span className="gv2-time-block"><span className="gv2-time-label">Hace</span><span className="gv2-timer">{relojAlarma(c.desde, nowMs)}</span></span>
          </span>
          <span className="gv2-title block">{eventSentence(c.verbo)}</span>
          <span className="flex flex-wrap items-center gap-1.5">
            {c.es_tecnica && (
              <span data-testid={`radar-inst-caso-tecnica-${c.alarma_id}`}
                className="gv2-type">
                <Wrench className="h-3 w-3" /> Técnico
              </span>
            )}
          </span>
          <span className="gv2-info-line gv2-person mt-2">
            <UserRound aria-hidden="true" /><span>{c.persona_nombre || "Residente sin identificar"}</span>
          </span>
          <span className="gv2-info-line gv2-meta gv2-location mt-1"><MapPin aria-hidden="true" /><span>{c.donde}</span></span>
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" data-testid={`radar-inst-caso-central-${c.alarma_id}`}
          onClick={() => onAbrirCaso(c)}
          className="gv2-primary ml-auto inline-flex min-h-9 items-center gap-1 rounded-lg px-2.5 text-[11px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]">
          Central <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
        {c.ubicacion?.zona_id && (
          <button type="button" data-testid={`radar-inst-caso-habitacion-${c.alarma_id}`}
            onClick={() => onVerHabitacion(c.ubicacion.zona_id)}
            title="Ver esta habitación en el Radar, aunque esté oculta por búsqueda, selección o página"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-[#012472] transition-colors hover:border-[#01CBE3]">
            <MapPin className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </article>
  );
}

export default function AtencionCentro({
  casos, resumen, lectura, nowMs, onAbrirCaso, onVerHabitacion, onSeleccionar, seleccionadoId,
  onMasCasos, hayMas = false, cargandoMas = false,
}) {
  const [expandida, setExpandida] = useState(false);
  // P2/P4-A · una página posterior con `completo:false` NO es un error por sí sola: el aviso de
  // lectura no confirmada sólo aparece cuando el servidor informó un error parcial.
  const parcial = !!lectura?.error_parcial;
  const totalAutorizado = resumen?.total ?? null;

  if (!casos.length) {
    return (
      <section data-testid="radar-inst-atencion" data-vacio="si" className="px-3.5 py-2.5">
        <p data-testid="radar-inst-atencion-vacio" className="flex flex-wrap items-center gap-x-2 text-[13px] font-semibold text-slate-500">
          <ShieldCheck className="h-4 w-4 shrink-0 text-[#0891A6]" />
          {parcial
            ? "No se pudo confirmar la lista de casos abiertos de este centro."
            : "Sin casos abiertos en este centro ahora mismo."}
          <span className="font-normal text-slate-400">
            {parcial ? "Reintentando; esto no significa cero casos." : "El Radar observa; no confirma la cobertura de los sensores."}
          </span>
        </p>
      </section>
    );
  }

  const mostrados = expandida ? casos : casos.slice(0, VISIBLES_COLAPSADO);
  const restantesEnPagina = casos.length - mostrados.length;
  // Lo que falta por LEER del servidor (no lo que falta por mostrar en pantalla).
  const noLeidos = totalAutorizado == null ? null : Math.max(0, totalAutorizado - casos.length);

  return (
    <section data-testid="radar-inst-atencion" className="p-3">
      <header className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="text-[13px] font-extrabold uppercase tracking-wider text-[#012472]">Atención del centro</h2>
        <p data-testid="radar-inst-atencion-conteo" className="text-[11px] font-semibold text-slate-500">          {resumen ? (
            <>
              {resumen.total} {resumen.total === 1 ? "caso abierto" : "casos abiertos"} ·{" "}
              {resumen.pendientes} pendiente{resumen.pendientes === 1 ? "" : "s"} ·{" "}
              {resumen.en_atencion} en atención
              <span className="hidden sm:inline">
                {" "}· {resumen.humanos} de persona · {resumen.tecnicos} técnico{resumen.tecnicos === 1 ? "" : "s"}
                {resumen.otros_estados > 0 ? ` · ${resumen.otros_estados} en otro estado` : ""}
              </span>
            </>
          ) : "Conteo de casos no confirmado"}
        </p>
      </header>
      {/* ALTURA CONTROLADA: la zona de atención nunca crece hasta tapar las habitaciones. Con
          muchos casos el bloque conserva su alto máximo y el resto se alcanza por desplazamiento
          y por el acceso explícito de abajo. */}
      <div className={expandida
        ? "grid max-h-[52vh] grid-cols-1 items-start gap-2 overflow-y-auto pr-1 sm:max-h-[46vh] sm:grid-cols-2 xl:grid-cols-3"
        : "grid max-h-[24vh] grid-cols-1 items-start gap-2 overflow-y-auto pr-1 sm:max-h-[38vh] sm:grid-cols-2 xl:grid-cols-3"}>
        {mostrados.map((c) => (
          <Fila key={c.alarma_id} c={c} nowMs={nowMs} onAbrirCaso={onAbrirCaso}
            onVerHabitacion={onVerHabitacion} onSeleccionar={onSeleccionar}
            seleccionado={seleccionadoId === c.alarma_id} />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {(restantesEnPagina > 0 || expandida) && (
          <button type="button" data-testid="radar-inst-atencion-mas" onClick={() => setExpandida((v) => !v)}
            className="gv2-chip-btn inline-flex items-center">
            {expandida
              ? <><span className="sm:hidden">Ver menos</span><span className="hidden sm:inline">Mostrar sólo los primeros {VISIBLES_COLAPSADO}</span> <ChevronUp className="h-3.5 w-3.5" /></>
              : <><span className="sm:hidden">Ver {restantesEnPagina} más</span><span className="hidden sm:inline">Ver los {restantesEnPagina} casos restantes</span> <ChevronDown className="h-3.5 w-3.5" /></>}
          </button>
        )}
        {(hayMas || (noLeidos != null && noLeidos > 0)) && (
          <button type="button" data-testid="radar-inst-atencion-pendientes-lectura" onClick={onMasCasos}
            disabled={cargandoMas}
            className="gv2-chip-btn inline-flex items-center disabled:opacity-50">
            {cargandoMas ? "Cargando…"
              : noLeidos ? (<><span className="sm:hidden">Cargar más · {casos.length}/{totalAutorizado}</span><span className="hidden sm:inline">Cargar más · {casos.length} de {totalAutorizado} casos del centro</span></>)
                : "Cargar más casos del centro"}
          </button>
        )}
        {noLeidos === 0 && casos.length > 0 && totalAutorizado != null && (
          <span data-testid="radar-inst-atencion-cargados" className="text-[11px] font-semibold text-slate-400">
            {casos.length} de {totalAutorizado} casos del centro cargados
          </span>
        )}
      </div>
      {parcial && (
        <p data-testid="radar-inst-lectura-parcial" className="mt-2 rounded-lg bg-white/80 px-2.5 py-1.5 text-[11px] font-semibold text-[#9B2C2C]">
          Lectura incompleta: se muestran {lectura.casos_devueltos} de {lectura.casos_total_consultados ?? "—"} casos autorizados.
          {lectura.error_parcial ? ` ${lectura.error_parcial}` : ""} Esto no significa cero casos.
        </p>
      )}
    </section>
  );
}
